import React, { useEffect, useState } from 'react';
import { demoStoreUrl, scrollToTemplates } from './Hero';
import { trackGetFreeClick } from '../utils/track';
import PreviewImage from './PreviewImage';
import './HeroSlider.css';

// Top-of-page banner: one slide per demo store design, built to match the stores' own hero (stores/plants Home.js).
// Each slide takes its store's colours (template.theme) and shows a screenshot of the demo store (template.preview).
// Slides change every 5 s, pause while the pointer or keyboard focus is on the banner, and do not auto-advance for
// visitors who prefer reduced motion.
const SLIDE_MS = 5000;
// How wide the screenshot is shown (HeroSlider.css), so the browser picks the right WebP copy. Keep public/index.html's
// preload of the first slide on the same sizes.
const PREVIEW_SIZES = '(max-width: 767px) calc(100vw - 62px), (max-width: 1199px) 620px, 670px';

export default function HeroSlider({ templates, onCreate }) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = templates.length;

  useEffect(() => {
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (paused || reducedMotion || count < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), SLIDE_MS);
    return () => clearInterval(timer);
  }, [paused, count]);

  // Load the next slide's screenshot in advance so it doesn't flash in when the slide changes
  useEffect(() => {
    const next = templates[(index + 1) % count];
    if (!next || !next.previewSet) return;
    const img = new Image();
    img.sizes = PREVIEW_SIZES;
    img.srcset = next.previewSet;
  }, [index, count, templates]);

  if (!count) return null;
  const t = templates[index];
  const go = (step) => setIndex((i) => (i + step + count) % count);
  const demoUrl = t.isMore ? '#select-website' : demoStoreUrl(t);
  const theme = t.theme || {};

  return (
    <section
      className="hs-slider"
      style={{ '--hs-green': theme.main, '--hs-soft': theme.soft, '--hs-warm': theme.warm }}
      aria-roledescription="carousel"
      aria-label="Website designs"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}
    >
      <div className="hs-glow hs-glow-a" />
      <div className="hs-glow hs-glow-b" />

      {/* Keyed by slide, so the text and the preview play their entrance animation on every change */}
      <div className="hs-copy" key={`copy-${t.id}`} aria-live={paused ? 'polite' : 'off'}>
        <span className="hs-eyebrow">{t.label} • Free website</span>
        <h1 className="hs-title">
          {t.isMore ? 'Get free website for any business' : `Get free website for your ${t.business}`}
        </h1>
        <p className="hs-desc">{t.note}</p>
        <div className="hs-btns">
          {t.isMore ? (
            <>
              <button type="button" className="hs-btn hs-btn-main" onClick={scrollToTemplates}>
                Explore 100+ designs <span aria-hidden="true">↓</span>
              </button>
              <button type="button" className="hs-btn hs-btn-ghost" onClick={onCreate}>
                Get free website <span aria-hidden="true">→</span>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="hs-btn hs-btn-main" onClick={onCreate}>
                Get Website <span aria-hidden="true">→</span>
              </button>
              <a className="hs-btn hs-btn-ghost" href={demoUrl} target="_blank" rel="noopener" onClick={trackGetFreeClick}>
                Use this design
              </a>
            </>
          )}
        </div>
        <div className="hs-stats">
          <span>✨ 7-Day Free Trial (₹0)</span>
          <span>💸 0% Sales Commission</span>
          <span>⚡ Live in 60 seconds</span>
          <span>📱 Direct UPI & WhatsApp Alerts</span>
        </div>
      </div>

      {/* The demo store in a browser window; it opens the live demo */}
      {t.isMore ? (
        <div className="hs-preview" key={`preview-${t.id}`}>
          <a
            className="hs-browser hs-browser-more"
            href="#select-website"
            onClick={(e) => {
              e.preventDefault();
              scrollToTemplates();
            }}
            aria-label="Explore all 100+ ready store designs"
          >
            <span className="hs-browser-bar" aria-hidden="true">
              <i /><i /><i />
              <span className="hs-browser-url">ownezzy.com/#select-website</span>
            </span>
            <div className="hs-more-mosaic">
              <div className="hs-more-tile">
                <img src="/previews/blouses-480.webp?v=8" alt="Sarees & Designer Blouses" width="480" height="492" />
                <span>🥻 Designer Blouses</span>
              </div>
              <div className="hs-more-tile">
                <img src="/previews/linens-480.webp?v=8" alt="Bedsheets & Home Linen" width="480" height="492" />
                <span>🛏️ Bed Linens</span>
              </div>
              <div className="hs-more-tile">
                <img src="/previews/pattu-480.webp?v=8" alt="Kids Pattu Pavadai" width="480" height="492" />
                <span>🧒 Pattu Pavadai</span>
              </div>
              <div className="hs-more-tile">
                <img src="/previews/resin-480.webp?v=8" alt="Resin Art & Flower Keepsakes" width="480" height="492" />
                <span>🌸 Resin Art</span>
              </div>
            </div>
          </a>
          <span className="hs-float-chip hs-chip-a" aria-hidden="true">{t.icon} {t.label}</span>
          <span className="hs-float-chip hs-chip-b" aria-hidden="true">🎯 Ready in minutes</span>
          <div className="hs-badge">
            Ready-made catalog
            <br />
            <b>100+ Store Designs</b>
            <a
              href="#select-website"
              onClick={(e) => {
                e.preventDefault();
                scrollToTemplates();
              }}
            >
              Explore all designs ↓
            </a>
          </div>
        </div>
      ) : (
        <div className="hs-preview" key={`preview-${t.id}`}>
          <a className="hs-browser" href={demoUrl} target="_blank" rel="noopener" aria-label={`Open the ${t.name} demo store`}>
            <span className="hs-browser-bar" aria-hidden="true">
              <i /><i /><i />
              <span className="hs-browser-url">{t.demo}.ownezzy.com</span>
            </span>
            <PreviewImage key={t.id} template={t} sizes={PREVIEW_SIZES} alt={`${t.name}, a ${t.label.toLowerCase()} website made with Ownezzy`} width="960" height="984" />
          </a>
          <span className="hs-float-chip hs-chip-a" aria-hidden="true">{t.icon} {t.label}</span>
          <span className="hs-float-chip hs-chip-b" aria-hidden="true">🛒 New order received</span>
          <div className="hs-badge">
            Ready-made design
            <br />
            <b>{t.name}</b>
            <a href={demoUrl} target="_blank" rel="noopener">View live demo ↗</a>
          </div>
        </div>
      )}

      {count > 1 && (
        <>
          <button type="button" className="hs-arrow hs-arrow-prev" onClick={() => go(-1)} aria-label="Previous design">‹</button>
          <button type="button" className="hs-arrow hs-arrow-next" onClick={() => go(1)} aria-label="Next design">›</button>
          <div className="hs-dots">
            {templates.map((item, i) => (
              item.isMore ? (
                <button
                  key={item.id}
                  type="button"
                  className={`hs-dot hs-dot-more${i === index ? ' on' : ''}${paused ? ' paused' : ''}`}
                  onClick={() => {
                    if (i === index) {
                      scrollToTemplates();
                    } else {
                      setIndex(i);
                    }
                  }}
                  aria-label="Show And more designs"
                  aria-current={i === index}
                >
                  <span className="hs-dot-more-txt">And more ↓</span>
                </button>
              ) : (
                <button
                  key={item.id}
                  type="button"
                  className={i === index ? `hs-dot on${paused ? ' paused' : ''}` : 'hs-dot'}
                  onClick={() => setIndex(i)}
                  aria-label={`Show ${item.name}`}
                  aria-current={i === index}
                />
              )
            ))}
          </div>
        </>
      )}
    </section>
  );
}
